import type { AxiosError, AxiosResponse } from "axios";
import axios from "axios";
import type {
  Articolo,
  Cliente,
  DettaglioStorico,
  Impostazioni, IncassiAgente,
  Ordine,
  OrdineArticolo,
  OrdineRiga,
  PartitaAperta,
  TestateStorico,
  ElencoClientiPerStorico,
  SalesStats,
  SalesStatsDetail,
  SalesStatsVendite
} from "@/types";
import { useRootStore } from "@/stores/root";
import * as Sentry from "@sentry/vue";

export interface jnoshowartgetResponse {
  cdart: string;
  dsart: string;
}

export interface WsResponse<T> {
  d: T | string;
}

export interface WSResponseND {
  Message: string;
  Success: boolean;
}

export interface WSResponseData<T> extends WSResponseND {
  Data: T;
}

export interface ISoapClient {
  Login(args: { sVeccdage: string; sVecmobilepsw: string }): Promise<WSResponseData<string>>;

  GetClienti(): Promise<WSResponseData<Cliente[]>>;

  jnoshowartget(args: { sVeccdage: string; sVeccdclf: string; sVeccddes: string }): Promise<jnoshowartgetResponse[]>;

  jnoshowartdel(args: { sVeccdage: string; sVeccdclf: string; sVeccddes: string; sVeccdart: string }): Promise<void>;

  GetPreordine(args: { sVeccdclf: string; sTipoOrdine: string }): Promise<WSResponseData<Articolo[]>>;

  PutPreordine(args: { sVeccdclf: string; ordine: OrdineRiga[]; sTipoOrdine: string }): Promise<WSResponseData<Ordine>>;

  GetRicercaArticolo(args: {
    sVeccdclf: string;
    sFilter: string;
    sTipoOrdine: string;
  }): Promise<WSResponseData<OrdineArticolo[]>>;

  PutCreaOrdineCliente(args: {
    sVeccdclf: string;
    ordine: Ordine;
    sVeccddes: string;
    sTipoOrdine: string;
  }): Promise<WSResponseData<string>>;

  GetFidoCliente(args: { sVeccdclf: string }): Promise<WSResponseData<number>>;

  GetBudgetOmaggiAgente(args: { sVeccdage: string }): Promise<WSResponseData<number>>;

  GetImpostazioni(): Promise<WSResponseData<Impostazioni[]>>;

  GetTestateStoricoDocumenti(args: {
    sVeccdclf: string | null;
    sTipoOrdine: string | null;
    sVeccddes: string | null;
    dDataDA: string | null;
    dDataA: string | null;
  }): Promise<WSResponseData<TestateStorico[]>>;

  GetElencoClientiPerStorico(args: {
    sTipoDocumento: string;
  }): Promise<WSResponseData<ElencoClientiPerStorico[]>>

  GetDettaglioStoricoDocumenti(args: {
    sVeccdclf: string;
    iVecnnmpro: number;
    sTipoDoc: string;
  }): Promise<WSResponseData<DettaglioStorico[]>>;

  PutNoShow(args: { sVeccdclf: string; sVeccdart: string; sVeccddes: string }): Promise<WSResponseND>;

  GetScadenziarioCliente(args: { sVeccdclf: string }): Promise<WSResponseData<PartitaAperta[]>>;

  ExportTable(args: { data: string[][], header: boolean }): Promise<WSResponseData<number[]>>;

  PutPagamenti(args: { pPagati: PartitaAperta[], sVeccdclf: string }): Promise<WSResponseData<PartitaAperta[]>>;

  PutIncassiAgente(): Promise<WSResponseData<IncassiAgente[]>>;

  GetIncassiAgente(): Promise<WSResponseData<IncassiAgente[]>>;

  GetSalesStats(args: { groupBy: "day" | "week" | "month" }): Promise<WSResponseData<SalesStats[]>>;

  GetSalesStatsByCustomerDest(args: { groupBy: "day" | "week" | "month", sVeccdclf: string, sVeccddes:string }): Promise<WSResponseData<SalesStats[]>>;

  GetSalesStatsByCustomer(args: { groupBy: "day" | "week" | "month", sVeccdclf: string }): Promise<WSResponseData<SalesStats[]>>;

  GetSalesStatsDetail(args: {t_start: string, t_end: string}): Promise<WSResponseData<SalesStatsDetail>>;

  GetTestateFatture(args: {sVeccdclf: string|null, dDataDA: string, dDataA: string}): Promise<WSResponseData<TestateStorico[]>>;

  GetStatsDatiVendite(args: {sVeccdclf:string, mInf: number, mSup: number}): Promise<WSResponseData<SalesStatsVendite[]>>;
}


export interface WebServiceConfig {
  baseUrl: string;
}

const defaultConfig: WebServiceConfig = { baseUrl: "./isales.asmx" };

function _getResponse<Fn extends keyof ISoapClient>(
  response: AxiosResponse<WsResponse<ISoapClient[Fn]>>
): ISoapClient[Fn] | void {
  if (response.data.d) {
    if (response.data.d === "True") {
      return true as never as ISoapClient[Fn];
    }
    if (response.data.d === "False") {
      return false as never as ISoapClient[Fn];
    }
    try {
      return JSON.parse(response.data.d as string) as ISoapClient[Fn];
    } catch (error) {
      return response.data.d as never as ISoapClient[Fn];
    }
  }
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(`${response.data}`, "text/xml");
  return JSON.parse(xmlDoc.getElementsByTagName("string")[0].childNodes[0].nodeValue as string) as ISoapClient[Fn];
}
const NO_LOADING_REQUESTS = ["GetSalesStats", "GetSalesStatsByCustomer", "GetSalesStatsByCustomerDest"];
export function useSoapClient(config?: WebServiceConfig): ISoapClient {
  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (error.response) {
        Sentry.captureException(JSON.stringify(error.response.data));
      } else {
        Sentry.captureException(error);
      }
      return Promise.reject(error);
    },
  );
  return new Proxy<ISoapClient | WebServiceConfig>(config || defaultConfig, {
    get<Fn extends keyof ISoapClient>(
      config: WebServiceConfig,
      fnName: Fn
    ): (args?: { [p: string]: string | number | null }) => Promise<ISoapClient[Fn] | void> {
      if (`${fnName}` === "toJSON") {
        // workaround bug nel debugger di vue
        return () => ({} as never);
      }
      return async (args?: { [key: string]: string | number | null }): Promise<ISoapClient[Fn] | void> => {
        const rootStore = useRootStore();
        const token = rootStore.AUTH_TOKEN_get;
        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authentication: token ?? "",
        };
        try {
          if (!NO_LOADING_REQUESTS.includes(fnName)) {
            rootStore.LOADING_set(true);
          }
          const url = `${config.baseUrl}/${fnName}`;
          // workaround. axios rimuovere l'header Content-Type se il body è vuoto
          // ma è necessario perchè la risposta sia in formato json
          // fuck microsoft!
          if (!args) {
            args = { fake: "body" };
          }
          const response = await axios.post<WsResponse<ISoapClient[Fn]>>(url, args, { headers });
          return _getResponse<Fn>(response);
        }catch (error) {
          Sentry.captureException(error);
        } finally {
          if (!NO_LOADING_REQUESTS.includes(fnName)) {
            rootStore.LOADING_set(false);
          }
        }
      };
    },
  }) as ISoapClient;
}

export default useSoapClient(defaultConfig);
